import React, { useEffect, useState } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { awsConfig } from '../aws-config';
import { signOut, handleOAuthCallback } from '../cognitoService';
import { useNavigate } from 'react-router-dom';
import { getClientName, isAllowedRedirectDomain } from '../utils/clientNames';
import Footer from '../components/Footer';

const poolData = {
  UserPoolId: awsConfig.userPoolId,
  ClientId: awsConfig.userPoolWebClientId
};
const userPool = new CognitoUserPool(poolData);

export default function Home() {
  const [username, setUsername] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [redirectToClient, setRedirectToClient] = useState('');
  const [errorRedirectClient, setErrorRedirectClient] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      if ((code || error) && !localStorage.getItem('oauth_processing')) {
        localStorage.setItem('oauth_processing', 'true');

        let redirectToClient = null;
        if (state) {
          try {
            const stateData = JSON.parse(decodeURIComponent(state));
            redirectToClient = stateData.redirectToClient;
          } catch {
            console.warn('Failed to parse state parameter');
          }
        }

        if (error) {
          setUsername('');
          setError(true);
          setCountdown(5);
          setErrorRedirectClient(redirectToClient || '');

          const timer = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(timer);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          setTimeout(() => {
            if (redirectToClient) {
              nav(`/login?client_id=${redirectToClient}`);
            } else {
              nav('/login');
            }
          }, 5000);

          localStorage.removeItem('oauth_processing');
          return;
        }

        if (code && redirectToClient) {
          if (!isAllowedRedirectDomain(redirectToClient)) {
            console.error('Invalid redirect domain');
            localStorage.removeItem('oauth_processing');
            nav('/login');
            return;
          }

          setUsername('');
          setRedirectToClient(redirectToClient);
          setSuccess(true);

          setTimeout(() => {
            const protocol = redirectToClient.startsWith('localhost') ? 'http' : 'https';
            const redirectUrl = new URL(`${protocol}://${redirectToClient}/auth/callback`);
            redirectUrl.searchParams.set('code', code);
            window.location.href = redirectUrl.toString();
          }, 2000);

          localStorage.removeItem('oauth_processing');
          return;
        }

        if (code) {
          try {
            await handleOAuthCallback();
            window.history.replaceState({}, document.title, window.location.pathname);
          } catch {
            console.error('OAuth callback failed');
            setError(true);
            setCountdown(5);
            setErrorRedirectClient(redirectToClient || '');

            const timer = setInterval(() => {
              setCountdown(prev => {
                if (prev <= 1) {
                  clearInterval(timer);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);

            setTimeout(() => {
              if (redirectToClient) {
                nav(`/login?client_id=${redirectToClient}`);
              } else {
                nav('/login');
              }
            }, 5000);

            localStorage.removeItem('oauth_processing');
            return;
          }
        }

        localStorage.removeItem('oauth_processing');
      }

      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        try {
          const response = await fetch(`https://${awsConfig.oauth.domain}/oauth2/userInfo`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });
          const userInfo = await response.json();
          setUsername(userInfo.email || userInfo.username || 'Użytkownik');
        } catch {
          console.error('Failed to fetch user info');
          setUsername('Użytkownik');
        }
        return;
      }

      const user = userPool.getCurrentUser();
      if (!user) {
        nav('/login');
        return;
      }
      user.getSession((err) => {
        if (err) {
          console.error('Session error');
          nav('/login');
          return;
        }
        setUsername(user.getUsername());
      });
    };

    checkAuth();
  }, [nav]);

  const handleSignOut = () => {
    signOut();
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
    nav('/login');
  };

  if (success) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ 
          maxWidth: 400, 
          width: '100%',
          padding: 40,
          backgroundColor: 'white',
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: 32 }}>
            <img 
              src="/logo.svg" 
              alt="Berg Logo" 
              style={{ 
                width: 120, 
                height: 'auto', 
                marginBottom: 16
              }}
            />
            <h1 style={{ 
              margin: 0, 
              fontSize: 24, 
              fontWeight: 600, 
              color: '#1e293b',
              marginBottom: 8 
            }}>
              Berg Unified Login
            </h1>
          </div>
          <div style={{
            textAlign: 'center'
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: '#84cc16',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: 24,
              color: 'white'
            }}>
              ✓
            </div>
            <div style={{
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 8,
              letterSpacing: '-0.01em',
              color: '#1e293b'
            }}>
              Authentication successful
            </div>
            <div style={{
              fontSize: 13,
              color: '#64748b',
              fontWeight: 400
            }}>
              Redirecting to {getClientName(redirectToClient) || 'client application'}...
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{ 
          maxWidth: 400, 
          width: '100%',
          padding: 40,
          backgroundColor: 'white',
          borderRadius: 16,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          textAlign: 'center'
        }}>
          <div style={{ 
            backgroundColor: '#fef2f2', 
            color: '#dc2626', 
            padding: 16, 
            borderRadius: 8, 
            fontSize: 16,
            border: '1px solid #fecaca',
            marginBottom: 20
          }}>
            ❌ Authentication failed
          </div>
          <button
            onClick={() => {
              if (errorRedirectClient) {
                nav(`/login?client_id=${errorRedirectClient}`);
              } else {
                nav('/login');
              }
            }}
            style={{
              width: '100%',
              padding: '12px 24px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Try again ({countdown}s)
          </button>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 320, margin: 'auto', padding: 20 }}>
      <h2>Witaj, {username}!</h2>
      <button onClick={handleSignOut} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
        </svg>
        Wyloguj się
      </button>
    </div>
  );
}
